import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/app/kit";
import { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export const FieldInput = ({ field, value, onChange, testPrefix }) => {
  const tid = `${testPrefix}-${field.key}`;
  if (field.type === "textarea") return <Textarea rows={field.rows || 5} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={field.disabled} data-testid={tid} className="bg-white font-mono text-xs" />;
  if (field.type === "switch") return <div><Switch checked={!!value} onCheckedChange={onChange} disabled={field.disabled} data-testid={tid} /></div>;
  if (field.type === "select")
    return (
      <Select value={value ?? undefined} onValueChange={onChange} disabled={field.disabled}>
        <SelectTrigger className="bg-white" data-testid={tid}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
        <SelectContent className="max-h-72">{field.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    );
  if (field.type === "checklist")
    return (
      <div className="grid sm:grid-cols-2 gap-2">
        {field.options.map(([v, l, disabled]) => {
          const on = (value || []).includes(v);
          return (
            <label key={v} className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer", on ? "border-sky-300 bg-sky-50/50" : "border-slate-200", disabled && "opacity-50 cursor-not-allowed")}>
              <Checkbox checked={on} disabled={disabled} onCheckedChange={(c) => onChange(c ? [...(value || []), v] : (value || []).filter((x) => x !== v))} data-testid={`${tid}-${v}`} />
              {l}
            </label>
          );
        })}
      </div>
    );
  return <Input type={field.type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={field.disabled} placeholder={field.placeholder} data-testid={tid} className={cn("bg-white", field.mono && "font-mono")} />;
};

export const FormDialog = ({ open, onOpenChange, title, description, fields, initial, onSubmit, submitLabel = "Enregistrer", testid = "form-dialog", wide, extra }) => {
  const [values, setValues] = useState(initial || {});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setValues(initial || {});
      setErrors({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    fields.forEach((f) => {
      const v = values[f.key];
      if (f.required && (v === undefined || v === null || String(v).trim() === "")) errs[f.key] = "Champ requis";
      else if (f.validate) {
        const m = f.validate(v, values);
        if (m) errs[f.key] = m;
      }
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("max-h-[90vh] overflow-y-auto", wide ? "max-w-3xl" : "max-w-lg")} data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <Field key={f.key} label={f.label} required={f.required} error={errors[f.key]} hint={f.hint} className={f.full || ["textarea", "checklist"].includes(f.type) ? "sm:col-span-2" : ""}>
                <FieldInput field={f} value={values[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} testPrefix={testid} />
              </Field>
            ))}
          </div>
          {extra && extra(values, setValues)}
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
